import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import SwfDetailsBody from "../otherServices/staffWelfareFund/SwfDetailsBody";
import {
  approveRejectSwf,
  fetchSwfFormConfig,
  findOneSwf,
  getApproverName,
  isSwfActionable,
  isSwfSubmitSuccess,
} from "../otherServices/staffWelfareFund/staffWelfare.service";

const StaffWelfareApproval: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { requestID } = useParams<{ requestID: string }>();

  const [detail, setDetail] = useState<any>(null);
  const [formConfig, setFormConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refId = requestID ? decodeURIComponent(requestID) : "";

  const L = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneSwf({ referenceNumber: refId });
        setDetail(data);
        if (data?.purpose) {
          try {
            setFormConfig(await fetchSwfFormConfig(data.purpose));
          } catch {
            setFormConfig(null);
          }
        }
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refId, t]);

  const actionable = isSwfActionable(detail?.requestStatus || detail?.status);

  const submitAction = async (flag: "A" | "R") => {
    if (!detail?.referenceNumber) {
      toast.error(t("failed_to_load_details"));
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await approveRejectSwf({
        ReferenceNumber: detail.referenceNumber,
        Approve_Reject_flag: flag,
        hRemarks: remarks,
        approverName: getApproverName(),
      });
      if (!isSwfSubmitSuccess(res)) {
        toast.error(res?.message || "Action failed");
        return;
      }
      toast.success(
        flag === "A"
          ? L("application_has_been_approved", "Application has been approved")
          : L("application_has_been_rejected", "Application has been rejected")
      );
      navigate(routes.todoWorklist.tasks);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Action failed");
    } finally {
      setSubmitting(false);
      setShowRejectConfirm(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {L("staff_welfare_fund_application", "Staff Welfare Fund Application")}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.todoWorklist.tasks}>{t("to_do_work_list")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("staff_welfare_fund_approval")}
                </li>
              </ol>
            </nav>
          </div>
          <Link to={routes.todoWorklist.tasks} className="btn btn-light">
            {t("cancel")}
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">{t("no_records_found")}</div>
          </div>
        ) : (
          <>
            <SwfDetailsBody detail={detail} formConfig={formConfig} />

            {actionable && (
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-header bg-white">
                  <h5 className="mb-0 fw-bold">{L("approver_remarks", "Approver Remarks")}</h5>
                </div>
                <div className="card-body">
                  <label className="form-label fw-semibold">{t("remarks") === "remarks" ? "Remarks" : t("remarks")}</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder={t("write_something")}
                  />
                </div>
              </div>
            )}

            {actionable && (
              <div className="d-flex justify-content-end gap-2 mb-4">
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={submitting}
                  onClick={() => setShowRejectConfirm(true)}
                >
                  {t("reject")}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={submitting}
                  onClick={() => submitAction("A")}
                >
                  {submitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  {t("approve")}
                </button>
              </div>
            )}
          </>
        )}

        {showRejectConfirm && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t("reject")}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowRejectConfirm(false)} />
                </div>
                <div className="modal-body">
                  {L("are_you_sure_want_to_reject", "Are you sure you want to reject?")}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" disabled={submitting} onClick={() => setShowRejectConfirm(false)}>
                    {t("cancel")}
                  </button>
                  <button type="button" className="btn btn-danger" disabled={submitting} onClick={() => submitAction("R")}>
                    {submitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    {t("reject")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffWelfareApproval;
